import { isSameMonth } from 'date-fns';
import { formatDate, getUTCDate } from '../../core/helpers';
import CountryLabel from '../../components/CountryLabel/CountryLabel';
import './SellerCalendar.scss';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface CalendarPoint {
  date: Date;
  countries: string[];
}

export interface CalendarItem {
  items: CalendarPoint[];
  month: Date;
}

interface Props {
  data: CalendarItem;
  className?: string;
}

export default function SellerCalendar({
  data,
  className = '',
  ...rest
}: Props) {
  const { vs2912CpPrototypeV2 } = useFlags();
  const today = getUTCDate();
  const todayDate = today.getDate();
  const isCurrentMonth = isSameMonth(today, data.month);

  const getPosition = () => {
    if (!isCurrentMonth) return 0;

    const curIndex = data.items.findIndex(
      (c) => c.date.getDate() === todayDate
    );

    if (curIndex !== -1) {
      return (curIndex + 1) * 40 - 20;
    }

    const endIndex = data.items.findIndex((c) => c.date.getDate() > todayDate);

    if (endIndex === -1) {
      return '100%';
    }

    const start = data.items[endIndex - 1]?.date.getDate();
    const end = data.items[endIndex]?.date.getDate();
    const endPoint = (endIndex + 1) * 40 - 20;

    if (start) {
      const startPoint = endIndex * 40 - 20;
      const n = ((endPoint - startPoint) * 1000) / ((end - start) * 1000);
      return startPoint + n * (todayDate - start);
    }

    const n = (20 * 1000) / (end * 1000);
    return endPoint - n * (end - todayDate);
  };

  const testId = ({ ...rest } as any)['data-testid'];

  return (
    <div className={`SellerCalendar ${className}`} {...rest}>
      <div
        className='fw-600'
        data-testid={testId ? `${testId}-vat-calendar-month` : undefined}
      >
        {formatDate(data.month, 'MMMM yyyy')}
      </div>
      <div className='position-relative my-2'>
        {data.items
          .sort((a, b) => a.date.getTime() - b.date.getTime())
          .map((p, i) => (
            <div
              className='d-flex align-items-center py-2'
              key={p.date.getDate()}
            >
              <div
                className='SellerCalendar__date'
                data-testid={
                  testId
                    ? `${testId}-vat-calendar-item-${i + 1}-date`
                    : undefined
                }
              >
                {formatDate(p.date, 'do')}
              </div>

              <div className='row row-cols-auto'>
                {p.countries.map((item, n) => (
                  <div className='col d-flex align-items-center' key={item}>
                    <CountryLabel
                      data-testid={
                        testId
                          ? `${testId}-vat-calendar-item-${i + 1}-country-${
                              n + 1
                            }`
                          : undefined
                      }
                      country={item}
                      className='me-2'
                      link={vs2912CpPrototypeV2}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        {isCurrentMonth && (
          <div
            className='SellerCalendar__indicator position-absolute'
            style={{
              top: getPosition(),
            }}
          >
            <div className='SellerCalendar__circle rounded-circle position-absolute' />
            <div className='SellerCalendar__line' />
          </div>
        )}
      </div>
    </div>
  );
}
